import { useEffect, useState } from "react";
import { Navigate, Route, Routes, useParams } from "react-router-dom";

import EnterpriseLandingPage from "./landing/EnterpriseLandingPage";
import LoginPage from "./LoginPage";
import SignupPage from "./SignupPage";
import ForgotPassword from "./ForgotPassword";
import ResetPassword from "./ResetPassword";
import PrivateApp from "./PrivateApp";
import AdminDashboard from "./AdminDashboard";
import SignalDashboardFromCache from "./SignalDashboardFromCache";
import MTFLatestTradingSignalsFromCache from "./MTFLatestTradingSignalsFromCache";
import MtfSignalDashboardV2 from "./MtfSignalDashboardV2";
import GlossaryPage from "./GlossaryPage";
import OrderHistoryPage from "./OrderHistoryPage";
import AppErrorBoundary from "./AppErrorBoundary";
import { getAuthSnapshot, getAuthToken, subscribeToAuthChanges } from "./auth/authUtils";
import { buildApiUrl, getApiHeaders } from "./config";
import { WorkspaceProvider } from "./workspace/WorkspaceContext";
import {
  buildScopedPath,
  getStoredWorkspace,
  resolveWorkspaceFromRoute,
  resolveWorkspace,
} from "./workspace/workspaceScope";

function WorkspaceRoute({ children }) {
  const params = useParams();
  const workspace = resolveWorkspaceFromRoute(params);

  if (!workspace?.exchange || !workspace?.marketType) {
    console.debug("WorkspaceRoute waiting for workspace params", params);
    return <div className="app-loading-state">Loading workspace...</div>;
  }

  console.debug("WorkspaceRoute resolved workspace", workspace, params);

  return (
    <WorkspaceProvider initialWorkspace={workspace}>
      {children}
    </WorkspaceProvider>
  );
}

function LegacyWorkspaceRedirect({ basePath, suffix = "" }) {
  const [target, setTarget] = useState(null);
  const [scopeError, setScopeError] = useState("");
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setScopeError("");
    async function resolveTarget() {
      const stored = getStoredWorkspace();
      try {
        const response = await fetch(buildApiUrl('/api/bot/ui/orchestration/symbol-universe/scopes'), {
          headers: getApiHeaders({ Authorization: `Bearer ${getAuthToken()}` }),
          cache: 'no-store',
        });
        if (!response.ok) throw new Error(`Scope API returned ${response.status}`);
        const scopes = await response.json();
        if (!Array.isArray(scopes)) throw new Error('Scope API returned an invalid response');
        const validScopes = scopes.filter(scope => scope && typeof scope === "object")
          .map(resolveWorkspace).filter(scope => scope.exchange && scope.marketType);
        const selected = validScopes.find(scope =>
          scope.exchange === stored.exchange && scope.marketType === stored.marketType) || validScopes[0];
        if (!selected) throw new Error('No active processing scope is configured');
        if (!cancelled) setTarget(buildScopedPath(basePath, selected, suffix));
      } catch (error) {
        if (stored.exchange && stored.marketType) {
          if (!cancelled) setTarget(buildScopedPath(basePath, stored, suffix));
        } else if (!cancelled) {
          setScopeError(error.message || 'Unable to load processing scopes');
        }
      }
    }
    resolveTarget();
    return () => { cancelled = true; };
  }, [basePath, suffix, retry]);

  if (target) return <Navigate to={target} replace />;
  if (scopeError) return (
    <div className="app-loading-state" role="alert">
      Unable to open your workspace: {scopeError}
      <button type="button" onClick={() => setRetry(value => value + 1)}>Retry</button>
    </div>
  );
  return <div className="app-loading-state">Loading workspace...</div>;
}

function ScopedPrivateApp() {
  return (
    <WorkspaceRoute>
      <PrivateApp />
    </WorkspaceRoute>
  );
}

function ScopedSignalPreview({ mtf = false }) {
  return (
    <WorkspaceRoute>
      <div style={{ minHeight: "100vh", background: "#f8fafc" }}>
        <div
          style={{
            position: "sticky",
            top: 0,
            zIndex: 30,
            display: "flex",
            justifyContent: "flex-end",
            padding: "10px 16px",
            background: "#020817",
            borderBottom: "1px solid rgba(14, 165, 233, 0.35)",
          }}
        >
          <button
            className="header-btn"
            onClick={() => {
              window.location.href = "/";
            }}
            type="button"
          >
            Close
          </button>
        </div>
        {mtf ? (
          <MTFLatestTradingSignalsFromCache isPublic={true} />
        ) : (
          <SignalDashboardFromCache isPublic={true} />
        )}
      </div>
    </WorkspaceRoute>
  );
}

function ScopedOrderHistoryPage() {
  return (
    <WorkspaceRoute>
      <OrderHistoryPage />
    </WorkspaceRoute>
  );
}

function ScopedMtfSignalV2Preview() {
  return (
    <WorkspaceRoute>
      <div style={{ minHeight: "100vh", background: "#eaf9fb" }}>
        <div
          style={{
            position: "sticky",
            top: 0,
            zIndex: 30,
            display: "flex",
            justifyContent: "flex-end",
            padding: "10px 16px",
            background: "#fff",
            borderBottom: "1px solid #b8e8ef",
          }}
        >
          <button className="header-btn" onClick={() => { window.location.href = "/"; }} type="button">
            Close V2 Dashboard
          </button>
        </div>
        <MtfSignalDashboardV2 publicAccess />
      </div>
    </WorkspaceRoute>
  );
}

export default function App() {
  const [authState, setAuthState] = useState(() => getAuthSnapshot());
  const { isLoggedIn, roles } = authState;
  const defaultAppPath = "/app";

  useEffect(() => {
    return subscribeToAuthChanges((nextAuthState) => {
      console.debug("App auth state updated", nextAuthState);
      setAuthState(nextAuthState);
    });
  }, []);

  return (
    <AppErrorBoundary>
      <Routes>
        <Route path="/" element={<EnterpriseLandingPage />} />
        <Route
          path="/login"
          element={isLoggedIn ? <Navigate to={defaultAppPath} replace /> : <LoginPage />}
        />
        <Route
          path="/signup"
          element={isLoggedIn ? <Navigate to={defaultAppPath} replace /> : <SignupPage />}
        />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/glossary" element={<GlossaryPage />} />

        <Route path="/signals" element={<LegacyWorkspaceRedirect basePath="/signals" />} />
        <Route
          path="/signals/:exchange/:marketType"
          element={<ScopedSignalPreview mtf={false} />}
        />

        <Route path="/mtf-signals" element={<LegacyWorkspaceRedirect basePath="/mtf-signals" />} />
        <Route
          path="/mtf-signals/:exchange/:marketType"
          element={<ScopedSignalPreview mtf={true} />}
        />

        <Route path="/mtf-signals-v2" element={<LegacyWorkspaceRedirect basePath="/mtf-signals-v2" />} />
        <Route
          path="/mtf-signals-v2/:exchange/:marketType"
          element={<ScopedMtfSignalV2Preview />}
        />

        <Route
          path="/app"
          element={
            isLoggedIn ? (
              <LegacyWorkspaceRedirect basePath="/app" suffix="dashboard" />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/app/:exchange/:marketType/*"
          element={isLoggedIn ? <ScopedPrivateApp /> : <Navigate to="/login" replace />}
        />
        <Route
          path="/app/:exchange/:marketType/order-history/:signalRequestId"
          element={isLoggedIn ? <ScopedOrderHistoryPage /> : <Navigate to="/login" replace />}
        />
        <Route
          path="/order-history/:signalRequestId"
          element={
            isLoggedIn ? (
              <LegacyWorkspaceRedirect basePath="/app" suffix={`/order-history/${window.location.pathname.split('/').pop() || ''}`} />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />

        <Route
          path="/admin"
          element={
            isLoggedIn && roles.includes("ROLE_ADMIN")
              ? <AdminDashboard />
              : <Navigate to="/" replace />
          }
        />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AppErrorBoundary>
  );
}

